// filepath: apps/web/src/features/admin/tier/types.ts
//
// FR-UI-ADM-14 — TypeScript wire-type mirrors for the Tier & Quota
// Admin panel. Mirrors the Go wire types in
//   internal/kernel/tier_handler.go
// (TierQuotaResponse, TierLimitsDTO, TierUsageDTO).
//
// INV-9: all server-supplied strings render as text nodes only.
// INV-1: the cache key MUST include tenantSlug as the 2nd segment.

/**
 * TierLimits — the per-tier limit block. Field set fixed by
 * docs/requirements/FR-UI-ADM-14 §3.1.
 *
 * Mirrors kernel.TierLimitsDTO in internal/kernel/tier_handler.go.
 * Field additions require an FR; field removal/rename requires a
 * coordinated SPA + backend change.
 */
export interface TierLimits {
  /** FR-TIER-2 AC-1: per-script wall-clock limit (ms). */
  script_wall_clock_ms: number;
  /** FR-TIER-2 AC-2: per-script Wasm linear-memory page cap. */
  script_memory_pages: number;
  /** FR-TIER-3 AC-1: dynamic-data storage quota (MB). */
  storage_mb: number;
  /** FR-TIER-3 AC-2: file storage quota (MB). */
  file_storage_mb: number;
  /** FR-TIER-3 AC-3: concurrent-sandboxes quota (count). */
  concurrent_sandboxes: number;
  /** FR-TIER-5: agent retry budget N. */
  agent_retry_budget: number;
  /** FR-FILE-2: single-upload size cap (MB). */
  max_single_upload_mb: number;
  /** FR-GRO-4: outbox depth cap. */
  outbox_depth_cap: number;
}

/**
 * TierUsage — the per-tenant usage block. concurrent_sandboxes may
 * be -1 ("unavailable" sentinel) when the SandboxCounter is not yet
 * wired (graceful degradation per FR-UI-ADM-14 §3.1).
 */
export interface TierUsage {
  /** Bytes of dynamic-data storage currently consumed. */
  storage_bytes: number;
  /** Bytes of file storage currently consumed. */
  file_storage_bytes: number;
  /** Concurrent sandboxes currently claimed; -1 = unavailable. */
  concurrent_sandboxes: number;
}

/**
 * TierQuotaResponse — wire shape returned by GET /api/admin/tier.
 * Mirrors kernel.TierQuotaResponse in internal/kernel/tier_handler.go.
 */
export interface TierQuotaResponse {
  tier: "small" | "middle" | "corporate";
  limits: TierLimits;
  usage: TierUsage;
}

/**
 * TierFetchState — the 5 fetch states the panel surfaces (FR-UI-CORE-10
 * AC-5 + AC-6). The page maps each `kind` to its dedicated UI block.
 *
 *   - "loading"          — fetch in flight.
 *   - "success"          — TierQuotaResponse received.
 *   - "fetch-failure"    — 5xx, network failure, or generic fetch error.
 *   - "permission-denied" — 401 / 403 (caller lacks `audit` or `*` admin perm).
 *   - "rate-limit"       — 429 backpressure.
 */
export type TierFetchState =
  "loading" | "success" | "fetch-failure" | "permission-denied" | "rate-limit";

/**
 * TierFetchError — typed envelope for the 3 non-success error states
 * (fetch-failure / permission-denied / rate-limit). The page renders
 * one of these with the matching chrome message.
 *
 *   - `kind: "permission-denied"` → no retry control.
 *   - `kind: "fetch-failure"`     → retry control offered.
 *   - `kind: "rate-limit"`        → retry control with Retry-After countdown.
 */
export interface TierFetchError {
  kind: "fetch-failure" | "permission-denied" | "rate-limit";
  message: string;
  retriable: boolean;
  /** Seconds remaining until the rate-limit window resets. */
  retryAfterSeconds?: number;
}

/**
 * ConcurrentSandboxesUnavailable — the documented "unavailable"
 * sentinel for the concurrent_sandboxes usage field. Mirrors
 * kernel.ConcurrentSandboxesUnavailable in internal/kernel/tier_handler.go.
 */
export const CONCURRENT_SANDBOXES_UNAVAILABLE = -1;
